import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  ChevronDown,
  Check,
  X,
  FileUp,
  Info,
  Calendar,
  Layers,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';

// --- Types ---

type ImportOptions = {
  file: File | null;
  destinationFolder: string;
  skipDuplicates: boolean;
  tagImports: boolean;
};

interface ParsedFolder {
  type: 'folder';
  title: string;
  /** Temporary client-side ID used for parent-child linking before server IDs exist */
  tid: string;
  pids?: string;
  tgid?: string;
  children: ParsedItem[];
}

interface ParsedBookmark {
  type: 'url';
  title: string;
  href: string;
  tgid?: string;
}

type ParsedItem = ParsedFolder | ParsedBookmark;

interface GroupBulkItem {
  title: string;
  tid: string;
  pinx?: string;
  inx: number;
  pids?: string;
}

interface ServerGroup {
  id: string;
  tid: string;
  pids?: string;
  pid?: string;
}

interface ApiStatusResponse {
  status: 'success' | 'error';
  message?: string;
}

// --- Bookmark HTML parser (replaces importParser + jQuery) ---

let _tidCounter = 0;
function generateTid() {
  return `t${++_tidCounter}`;
}

function parseDL(dl: Element): ParsedItem[] {
  const items: ParsedItem[] = [];
  dl.querySelectorAll(':scope > dt').forEach((dt) => {
    const h3 = dt.querySelector(':scope > h3');
    const a = dt.querySelector<HTMLAnchorElement>(':scope > a');
    const subDL = dt.querySelector(':scope > dl');
    if (h3) {
      items.push({
        type: 'folder',
        title: h3.textContent?.trim() || 'Untitled',
        tid: generateTid(),
        children: subDL ? parseDL(subDL) : [],
      });
    } else if (a) {
      items.push({
        type: 'url',
        title: a.textContent?.trim() || 'Untitled',
        href: a.getAttribute('href') || '',
      });
    }
  });
  return items;
}

function parseBookmarks(html: string): ParsedItem[] {
  _tidCounter = 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const rootDL = doc.querySelector('dl');
  if (!rootDL) return [];
  return [
    {
      type: 'folder',
      title: 'Imported Bookmarks',
      tid: generateTid(),
      children: parseDL(rootDL),
    },
  ];
}

// --- Sub-components ---

const Toggle = ({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (val: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={() => onChange(!enabled)}
    className={clsx(
      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      enabled ? 'bg-primary' : 'bg-muted'
    )}
  >
    <span
      className={clsx(
        'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        enabled ? 'translate-x-4' : 'translate-x-0'
      )}
    />
  </button>
);

const PreferenceRow = ({
  title,
  hint,
  enabled,
  onChange,
}: {
  title: string;
  hint: string;
  enabled: boolean;
  onChange: (val: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <div className="min-w-0 space-y-0.5">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
    </div>
    <Toggle enabled={enabled} onChange={onChange} />
  </div>
);

// --- Main Page Section ---

export const ImportBookmarksSettings = () => {
  const { t } = useTranslation('settings');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);
  const [options, setOptions] = useState<ImportOptions>({
    file: null,
    destinationFolder: '',
    skipDuplicates: true,
    tagImports: false,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  function collectGroups(
    items: ParsedItem[],
    gdata: GroupBulkItem[],
    parentPids: string | undefined,
    parentTid: string | undefined,
    parentInx: string | undefined
  ) {
    items.forEach((item, i) => {
      if (item.type === 'folder') {
        if (parentTid !== undefined)
          item.pids =
            parentPids !== undefined ? `${parentPids},${parentTid}` : parentTid;

        const entry: GroupBulkItem = { title: item.title, tid: item.tid, inx: i };
        if (parentInx !== undefined) entry.pinx = parentInx;
        if (item.pids !== undefined) entry.pids = item.pids;
        gdata.push(entry);

        collectGroups(
          item.children,
          gdata,
          item.pids,
          item.tid,
          parentInx !== undefined ? `${parentInx},${i}` : String(i)
        );
      } else if (parentTid !== undefined) {
        item.tgid = parentTid;
      }
    });
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setImportDone(false);
      setImportError(null);
    }
  };

  // --- Recursively collect all bookmarks from parsed tree after groups have server IDs ---
  const createTroves = async (
    serverGroups: ServerGroup[],
    parsedData: ParsedItem[]
  ) => {
    const troves: Array<{ title: string; url: string; groupid?: string; createdate: number }> = [];

    function collectTroves(items: ParsedItem[]) {
      items.forEach((item) => {
        if (item.type === 'url') {
          const group = serverGroups.find((g) => g.tid === item.tgid);
          troves.push({
            title: item.title,
            url: item.href,
            groupid: group?.id,
            createdate: Date.now(),
          });
        } else {
          collectTroves(item.children);
        }
      });
    }

    collectTroves(parsedData);

    if (troves.length === 0) return;

    setImportStatus(`Importing ${troves.length} bookmark(s)...`);
    const troveRes = await api.post<ApiStatusResponse>('/troveBulk', { troveArr: JSON.stringify(troves) });
    if (troveRes.data.status !== 'success') {
      throw new Error(troveRes.data.message ?? 'Failed to import bookmarks.');
    }
  };

  // --- Main import handler ---
  const handleStartImport = () => {
    if (!file) return;

    setIsImporting(true);
    setImportError(null);
    setImportDone(false);
    setImportStatus('Reading file...');

    const reader = new FileReader();

    reader.onload = async (evnt) => {
      try {
        const result = evnt.target?.result;
        if (typeof result !== 'string') throw new Error('Failed to read file.');

        setImportStatus('Parsing bookmarks...');
        const parseData = parseBookmarks(result);

        // Build flat list of folder groups with temporary IDs for bulk creation.
        // Include the synthetic root folder so root-level bookmarks get a tgid → groupid mapping.
        const gdata: GroupBulkItem[] = [];
        const root = parseData[0];
        if (root?.type === 'folder') {
          gdata.push({ title: root.title, tid: root.tid, inx: 0 });
          collectGroups(root.children, gdata, undefined, root.tid, '0');
        } else {
          collectGroups(parseData, gdata, undefined, undefined, undefined);
        }

        if (gdata.length > 500) {
          throw new Error("Can't import more than 500 folders at once.");
        }

        setImportStatus('Creating folders...');
        const res = await api.post<ApiStatusResponse & { groups?: ServerGroup[] }>('/groupBulk', {
          data: JSON.stringify(gdata),
        });
        if (res.data.status !== 'success') {
          throw new Error(res.data.message ?? 'Failed to create folders.');
        }

        const serverGroups: ServerGroup[] = res.data.groups ?? [];

        // Resolve real parent IDs from temporary tids
        const grparr: { id: string; parentid: string }[] = [];
        serverGroups.forEach((group) => {
          if (group.pids) {
            const parentIds = group.pids
              .split(',')
              .map((tid) => serverGroups.find((g) => g.tid === tid)?.id)
              .filter((id): id is string => id !== undefined);
            group.pid = parentIds.join(',');
            grparr.push({ id: group.id, parentid: group.pid });
          }
        });

        if (grparr.length > 0) {
          setImportStatus('Linking folder hierarchy...');
          const linkRes = await api.put<ApiStatusResponse>('/groupBulk', { grpArr: JSON.stringify(grparr) });
          if (linkRes.data.status !== 'success') {
            throw new Error(linkRes.data.message ?? 'Failed to link folder hierarchy.');
          }
        }

        await createTroves(serverGroups, parseData);

        setImportStatus('Import complete!');
        setImportDone(true);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Import failed. Please try again.';
        setImportError(msg);
      } finally {
        setIsImporting(false);
      }
    };

    reader.onerror = () => {
      setImportError('Could not read the file.');
      setIsImporting(false);
    };

    reader.readAsText(file);
  };

  return (
    <div className="max-w-3xl space-y-6 pb-8">
      <header className="space-y-1">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">{t('importBookmarks.title')}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">{t('importBookmarks.description')}</p>
      </header>

      <section className="space-y-3">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={clsx(
            'relative flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-4 py-10 transition-all',
            isDragging
              ? 'border-primary bg-primary/5'
              : file
                ? 'border-emerald-500/40 bg-emerald-500/[0.03]'
                : 'border-border-muted bg-card hover:bg-muted/20'
          )}
        >
          <div
            className={clsx(
              'flex h-12 w-12 items-center justify-center rounded-xl transition-colors',
              file ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
            )}
          >
            {file ? <Check size={22} /> : <Upload size={22} />}
          </div>

          <div className="space-y-1 text-center">
            <p className="text-sm font-semibold text-foreground">
              {file ? file.name : t('importBookmarks.dropHere')}
            </p>
            <p className="text-xs text-muted-foreground">
              {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : t('importBookmarks.clickBrowse')}
            </p>
          </div>

          {!file ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {t('importBookmarks.browseFiles')}
            </button>
          ) : null}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".html"
            className="hidden"
          />

          {file ? (
            <button
              type="button"
              onClick={() => setFile(null)}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t('importBookmarks.cancel')}
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
          <Info size={12} aria-hidden />
          {t('importBookmarks.maxFileSize')}
        </p>
      </section>

      <section className="rounded-2xl bg-card p-4 sm:p-5">
        <div className="mb-4 flex items-center gap-2">
          <Layers size={16} className="text-primary" aria-hidden />
          <h3 className="text-sm font-bold text-foreground">{t('importBookmarks.importIntoFolder')}</h3>
        </div>
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-xl bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-muted/60"
        >
          <span>{t('importBookmarks.selectFolder')}</span>
          <ChevronDown size={16} className="opacity-60" />
        </button>
        <p className="mt-2 text-xs text-muted-foreground">{t('importBookmarks.folderHelp')}</p>

        <div className="mt-5 border-t border-border-muted pt-1">
          <div className="mb-1 flex items-center gap-2">
            <Calendar size={16} className="text-primary" aria-hidden />
            <h3 className="text-sm font-bold text-foreground">{t('importBookmarks.preferences')}</h3>
          </div>
          <PreferenceRow
            title={t('importBookmarks.skipDuplicates')}
            hint={t('importBookmarks.skipDuplicatesHint')}
            enabled={options.skipDuplicates}
            onChange={(val) => setOptions((prev) => ({ ...prev, skipDuplicates: val }))}
          />
          <div className="border-t border-border-muted">
            <PreferenceRow
              title={t('importBookmarks.tagImports')}
              hint={t('importBookmarks.tagImportsHint')}
              enabled={options.tagImports}
              onChange={(val) => setOptions((prev) => ({ ...prev, tagImports: val }))}
            />
          </div>
        </div>
      </section>

      {(isImporting || importError || importDone) && (
        <div
          className={clsx(
            'flex items-start gap-2.5 rounded-xl px-4 py-3 text-sm',
            importError
              ? 'bg-destructive/10 text-destructive'
              : importDone
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'bg-muted text-muted-foreground'
          )}
          role="status"
        >
          {isImporting && <Loader2 size={15} className="mt-0.5 shrink-0 animate-spin" />}
          {importError && <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
          {importDone && <Check size={15} className="mt-0.5 shrink-0" />}
          <span>{importError ?? importStatus}</span>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          disabled={isImporting}
          onClick={() => {
            setFile(null);
            setImportError(null);
            setImportStatus('');
            setImportDone(false);
          }}
          className="px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
        >
          {t('importBookmarks.cancel')}
        </button>
        <button
          type="button"
          onClick={handleStartImport}
          disabled={!file || isImporting}
          className={clsx(
            'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            file && !isImporting
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'cursor-not-allowed bg-muted text-muted-foreground/50'
          )}
        >
          {isImporting ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {importStatus || t('importBookmarks.importing')}
            </>
          ) : (
            <>
              <FileUp size={16} />
              {t('importBookmarks.startImport')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

